<div class="order-management">
  <!-- Header Section -->
  <div class="row mb-4 align-items-center">
    <div class="col-md-5">
      <h4>Order Management</h4>
      <p class="text-muted">Manage your store's orders and customer transactions</p>
    </div>
    <div class="col-md-7">
      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <!-- Auto-refresh controls -->
        <div class="auto-refresh-bar d-flex align-items-center gap-2 me-2">
          <nb-toggle
            [(checked)]="autoRefreshEnabled"
            (checkedChange)="toggleAutoRefresh()"
            status="primary">
            Auto-refresh
          </nb-toggle>

          <nb-select
            [(selected)]="refreshIntervalSec"
            (selectedChange)="onRefreshIntervalChange()"
            size="small"
            placeholder="Interval">
            <nb-option [value]="15">15 s</nb-option>
            <nb-option [value]="30">30 s</nb-option>
            <nb-option [value]="60">1 min</nb-option>
            <nb-option [value]="120">2 min</nb-option>
            <nb-option [value]="300">5 min</nb-option>
          </nb-select>

          <span *ngIf="autoRefreshEnabled" class="text-muted small text-nowrap">
            Next in {{ countdown }}s
          </span>

          <button
            class="btn btn-outline-secondary btn-sm text-nowrap"
            (click)="loadOrders(); loadOrderStatistics()">
            <nb-icon icon="refresh-outline"></nb-icon>
            Refresh Now
          </button>
        </div>

        <nb-select
          [(selected)]="exportFormat"
          size="small"
          placeholder="Format"
          class="me-2">
          <nb-option value="csv">CSV</nb-option>
          <nb-option value="xlsx">XLSX</nb-option>
        </nb-select>

        <button *ngIf="role !== 'receipt_auditor'" class="btn btn-success" (click)="exportOrders()">
          <nb-icon icon="download-outline"></nb-icon>
          Export Orders
        </button>
      </div>
    </div>
  </div>

  <!-- Export Filters -->
  <nb-card class="mb-4" *ngIf="role !== 'receipt_auditor'">
    <nb-card-header>{{ 'KPI.ORDERS.EXPORT_FILTERS' | translate }}</nb-card-header>
    <nb-card-body>
      <app-report-filter-bar
        [showStoreFilter]="false"
        (filterChange)="onExportFilterChange($event)"
        (exportRequested)="onExportOrders($event)">
      </app-report-filter-bar>
      <div class="row g-2 mt-3">
        <div class="col-md-3">
          <label class="form-label">{{ 'KPI.FINANCIAL.PAYMENT_STATUS' | translate }}</label>
          <select class="form-select" [(ngModel)]="exportPaymentStatus">
            <option value="">{{ 'KPI.ORDERS.ALL_PAYMENTS' | translate }}</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">{{ 'KPI.FINANCIAL.PAYMENT_METHOD' | translate }}</label>
          <select class="form-select" [(ngModel)]="exportPaymentMethod">
            <option value="">{{ 'KPI.ORDERS.ALL_METHODS' | translate }}</option>
            <option value="card">Card</option>
            <option value="mobilepay">MobilePay</option>
            <option value="cash">Cash</option>
          </select>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Statistics Cards -->
  <div class="row mb-4" *ngIf="orderStatistics">
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Total Orders</h6>
          <h4 class="text-primary">{{ orderStatistics.currentPeriod?.totalOrders || 0 }}</h4>
          <small class="text-success" *ngIf="orderStatistics.growth?.totalOrders > 0">
            +{{ orderStatistics.growth.totalOrders }}%
          </small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Total Revenue</h6>
          <h4 class="text-success">{{ formatCurrency(orderStatistics.currentPeriod?.totalRevenue || 0) }}</h4>
          <small class="text-success" *ngIf="orderStatistics.growth?.totalRevenue > 0">
            +{{ orderStatistics.growth.totalRevenue }}%
          </small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Avg Order Value</h6>
          <h4 class="text-info">{{ formatCurrency(orderStatistics.currentPeriod?.averageOrderValue || 0) }}</h4>
          <small class="text-success" *ngIf="orderStatistics.growth?.averageOrderValue > 0">
            +{{ orderStatistics.growth.averageOrderValue }}%
          </small>
        </nb-card-body>
      </nb-card>
    </div>
    <div class="col-md-3">
      <nb-card>
        <nb-card-body class="text-center">
          <h6 class="text-muted">Completion Rate</h6>
          <h4 class="text-warning">{{ orderStatistics.currentPeriod?.conversionRate || 0 }}%</h4>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Search and Filters -->
  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-center">
        <!-- Search -->
        <div class="col-md-3">
          <div class="input-group">
            <input 
              type="text" 
              class="form-control" 
              placeholder="Search by order number..." 
              [(ngModel)]="orderNumberSearch"
              (keyup.enter)="onFilterChange()">
            <button class="btn btn-outline-secondary" type="button" (click)="onFilterChange()">
              <nb-icon icon="search-outline"></nb-icon>
            </button>
          </div>
        </div>

        <div class="col-md-3">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Search by customer..." 
            [(ngModel)]="customerSearch"
            (keyup.enter)="onFilterChange()">
        </div>

        <!-- Sort -->
        <div class="col-md-2">
          <select class="form-select" [(ngModel)]="sortBy" (change)="onSortChange()">
            <option value="orderDate">Order Date</option>
            <option value="finalAmount">Amount</option>
            <option value="status">Status</option>
            <option value="orderNumber">Order Number</option>
          </select>
        </div>

        <div class="col-md-2">
          <select class="form-select" [(ngModel)]="sortOrder" (change)="onSortChange()">
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <!-- Filter Toggle -->
        <div class="col-md-2 text-end">
          <button class="btn btn-outline-secondary me-2" (click)="toggleFilters()">
            <nb-icon icon="options-outline"></nb-icon>
            Filters
          </button>
        </div>
      </div>

      <!-- Advanced Filters -->
      <div *ngIf="showFilters" class="row mt-3 pt-3 border-top">
        <div class="col-md-2">
          <label class="form-label">Status</label>
          <select class="form-select" [(ngModel)]="selectedStatus" (change)="onFilterChange()">
            <option value="">All Status</option>
            <option *ngFor="let status of orderStatuses" [value]="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        
        <div class="col-md-2">
          <label class="form-label">Payment Status</label>
          <select class="form-select" [(ngModel)]="selectedPaymentStatus" (change)="onFilterChange()">
            <option value="">All Payments</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        <div class="col-md-2">
          <label class="form-label">Start Date</label>
          <input 
            type="date" 
            class="form-control" 
            [(ngModel)]="startDate"
            (change)="onFilterChange()">
        </div>

        <div class="col-md-2">
          <label class="form-label">End Date</label>
          <input 
            type="date" 
            class="form-control" 
            [(ngModel)]="endDate"
            (change)="onFilterChange()">
        </div>

        <div class="col-md-4 d-flex flex-column justify-content-end gap-2">
          <nb-checkbox
            [(checked)]="filterLateWebhookAfterCancel"
            (checkedChange)="onFilterChange()">
            Show only late payment after cancel
          </nb-checkbox>
          <button class="btn btn-outline-secondary w-100" (click)="clearFilters()">
            Clear Filters
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Bulk Actions -->
  <div *ngIf="selectedOrders.size > 0 && role !== 'receipt_auditor'" class="bulk-actions mb-3">
    <nb-card>
      <nb-card-body class="py-2">
        <div class="row align-items-center">
          <div class="col-md-6">
            <span class="me-3">{{ selectedOrders.size }} orders selected</span>
          </div>
          <div class="col-md-6">
            <div class="row">
              <div class="col-md-8">
                <select class="form-select" [(ngModel)]="bulkAction">
                  <option value="">Select Action</option>
                  <option value="update_status">Update Status</option>
                  <option value="mark_urgent">Mark as Urgent</option>
                  <option value="add_notes">Add Notes</option>
                </select>
              </div>
              <div class="col-md-4">
                <button class="btn btn-primary w-100" (click)="executeBulkAction()">
                  Execute
                </button>
              </div>
            </div>
          </div>
        </div>
      </nb-card-body>
    </nb-card>
  </div>

  <!-- Orders Table -->
  <nb-card>
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th *ngIf="role !== 'receipt_auditor'" style="width: 40px;">
                <nb-checkbox 
                  [checked]="selectedOrders.size === orders.length && orders.length > 0"
                  [indeterminate]="selectedOrders.size > 0 && selectedOrders.size < orders.length"
                  (checkedChange)="selectAllOrders()">
                </nb-checkbox>
              </th>
              <th>Order Number</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Terminal</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let order of orders; trackBy: trackByOrderId">
              <td *ngIf="role !== 'receipt_auditor'">
                <nb-checkbox 
                  [checked]="selectedOrders.has(order._id)"
                  (checkedChange)="toggleOrderSelection(order._id)">
                </nb-checkbox>
              </td>
              
              <td>
                <div class="order-info">
                  <strong>{{ order.orderNumber }}</strong>
                  <br>
                  <small class="text-muted">{{ order.items?.length || 0 }} items</small>
                </div>
              </td>
              
              <td>
                <div class="customer-info">
                  <strong>{{ order.customer?.name || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ order.customer?.email || 'N/A' }}</small>
                </div>
              </td>
              
              <td>
                <span>{{ formatDate(order.orderDate) }}</span>
              </td>
              
              <td>
                <strong>{{ formatCurrency(order.finalAmount) }}</strong>
              </td>
              
              <td>
                <div class="d-flex flex-column align-items-start gap-1">
                  <nb-badge 
                    [status]="getStatusColor(order.status)"
                    [text]="order.status | titlecase">
                  </nb-badge>
                  <span
                    *ngIf="order.payment?.lateWebhookAfterCancel"
                    class="badge bg-warning text-dark late-webhook-badge">
                    ⚠ Late payment after cancel
                  </span>
                </div>
              </td>
              
              <td>
                <span>{{ formatPaymentMethod(order.payment?.paymentMethod) }}</span>
              </td>

              <td>
                <span *ngIf="order.posTerminalName; else noTerminal">{{ order.posTerminalName }}</span>
                <ng-template #noTerminal><span class="text-muted">—</span></ng-template>
              </td>
              
              <td>
                <div class="action-buttons">
                  <button 
                    class="btn btn-sm btn-outline-primary me-1" 
                    (click)="viewOrderDetails(order)"
                    nbTooltip="View Details">
                    <nb-icon icon="eye-outline"></nb-icon>
                  </button>
                  
                  <button 
                    *ngIf="role !== 'receipt_auditor'"
                    class="btn btn-sm btn-outline-warning me-1" 
                    (click)="openStatusModal(order)"
                    nbTooltip="Update Status">
                    <nb-icon icon="edit-outline"></nb-icon>
                  </button>
                  
                  <button 
                    *ngIf="order.status !== 'refunded' && role !== 'receipt_auditor'"
                    class="btn btn-sm btn-outline-danger me-1" 
                    (click)="openRefundModal(order)"
                    nbTooltip="Process Refund">
                    <nb-icon icon="undo-outline"></nb-icon>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div *ngIf="loading" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
        <p class="mt-2">Loading orders...</p>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading && orders.length === 0" class="text-center p-4">
        <nb-icon icon="inbox-outline" class="empty-icon mb-3"></nb-icon>
        <h5>No orders found</h5>
        <p class="text-muted">Try adjusting your filters or check back later.</p>
      </div>

      <!-- Pagination -->
      <div *ngIf="totalPages > 1" class="d-flex justify-content-between align-items-center p-3 border-top">
        <div>
          <span class="text-muted">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalOrders) }} 
            of {{ totalOrders }} orders
          </span>
        </div>
        <div>
          <button 
            class="btn btn-outline-secondary me-2" 
            [disabled]="currentPage === 1"
            (click)="onPageChange(currentPage - 1)">
            Previous
          </button>
          <span class="me-2">Page {{ currentPage }} of {{ totalPages }}</span>
          <button 
            class="btn btn-outline-secondary" 
            [disabled]="currentPage === totalPages"
            (click)="onPageChange(currentPage + 1)">
            Next
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Order Details Modal -->
<nb-card *ngIf="showOrderDetails" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5>Order Details - {{ selectedOrder?.orderNumber }}</h5>
    <button class="btn btn-ghost" (click)="closeOrderDetails()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>
  <nb-card-body *ngIf="selectedOrder" class="modal-body">
    <!-- Order Information -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h6>Order Information</h6>
        <p><strong>Order Number:</strong> {{ selectedOrder.orderNumber }}</p>
        <p><strong>Status:</strong> 
          <nb-badge [status]="getStatusColor(selectedOrder.status)" [text]="selectedOrder.status | titlecase"></nb-badge>
        </p>
        <p><strong>Date:</strong> {{ formatDate(selectedOrder.orderDate) }}</p>
        <p><strong>Total Amount:</strong> {{ formatCurrency(selectedOrder.finalAmount) }}</p>
      </div>
      <div class="col-md-6">
        <h6>Customer Information</h6>
        <p><strong>Name:</strong> {{ selectedOrder.customer?.name || 'N/A' }}</p>
        <p><strong>Email:</strong> {{ selectedOrder.customer?.email || 'N/A' }}</p>
        <p><strong>Phone:</strong> {{ selectedOrder.customer?.phone || 'N/A' }}</p>
      </div>
    </div>

    <!-- Order Items -->
    <div class="mb-4">
      <h6>Order Items</h6>
      <div class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of selectedOrder.items">
              <td>{{ item.productSnapshot?.name || 'N/A' }}</td>
              <td>{{ item.productSnapshot?.sku || 'N/A' }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ formatCurrency(item.unitPrice) }}</td>
              <td>{{ formatCurrency(item.totalPrice) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Payment Information -->
    <div class="mb-4" *ngIf="selectedOrder.payment">
      <h6>Payment Information</h6>
      <p><strong>Method:</strong> {{ formatPaymentMethod(selectedOrder.payment?.paymentMethod) }}</p>
      <p><strong>Status:</strong> 
        <nb-badge [status]="getPaymentStatusColor(selectedOrder.payment.paymentStatus)" [text]="selectedOrder.payment.paymentStatus | titlecase"></nb-badge>
      </p>
      <p><strong>Transaction ID:</strong> {{ selectedOrder.payment.transactionId || 'N/A' }}</p>
      <p *ngIf="selectedOrder.payment.paidAt"><strong>Paid At:</strong> {{ formatDate(selectedOrder.payment.paidAt) }}</p>
    </div>

    <!-- Age Verification -->
    <div class="mb-4" *ngIf="selectedOrder?.ageVerifiedBy?.age">
      <h6>Age Verification</h6>
      <p>
        <span class="badge bg-success me-2">✓ Approved</span>
        <span class="badge"
          [ngClass]="{
            'bg-primary': selectedOrder.ageVerifiedBy.verificationType === 'nfc',
            'bg-info text-dark': selectedOrder.ageVerifiedBy.verificationType === 'hit2open',
            'bg-warning text-dark': selectedOrder.ageVerifiedBy.verificationType === 'ageproof',
            'bg-secondary': selectedOrder.ageVerifiedBy.verificationType !== 'nfc' && selectedOrder.ageVerifiedBy.verificationType !== 'hit2open' && selectedOrder.ageVerifiedBy.verificationType !== 'ageproof'
          }">
          <ng-container [ngSwitch]="selectedOrder.ageVerifiedBy.verificationType">
            <ng-container *ngSwitchCase="'nfc'">NFC Card</ng-container>
            <ng-container *ngSwitchCase="'hit2open'">QR – Hit2Open App</ng-container>
            <ng-container *ngSwitchCase="'ageproof'">QR – AgeProof</ng-container>
            <ng-container *ngSwitchDefault>QR</ng-container>
          </ng-container>
        </span>
      </p>
      <p><strong>Verified customer:</strong> {{ selectedOrder.ageVerifiedBy.name || 'Anonymous' }}</p>
      <p><strong>Age:</strong> {{ selectedOrder.ageVerifiedBy.age }} years</p>
      <p *ngIf="selectedOrder.ageVerifiedBy.email"><strong>Email:</strong> {{ selectedOrder.ageVerifiedBy.email }}</p>
      <p><strong>Verified at:</strong> {{ selectedOrder.ageVerifiedBy.verifiedAt | date:'dd/MM/yyyy HH:mm' }}</p>
    </div>
  </nb-card-body>
</nb-card>

<!-- Status Update Modal -->
<nb-card *ngIf="showStatusModal" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5>Update Order Status</h5>
    <button class="btn btn-ghost" (click)="closeStatusModal()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>
  <nb-card-body class="modal-body">
    <form [formGroup]="statusForm" (ngSubmit)="updateOrderStatus()">
      <div class="mb-3">
        <label class="form-label">Status *</label>
        <select class="form-control" formControlName="status" required>
          <option value="">Select Status</option>
          <option *ngFor="let status of orderStatuses" [value]="status.value">
            {{ status.label }}
          </option>
        </select>
        <div *ngIf="statusForm.get('status')?.invalid && statusForm.get('status')?.touched" class="text-danger">
          Status is required
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Admin Notes</label>
        <textarea class="form-control" formControlName="adminNotes" rows="3" 
                  placeholder="Add notes about this status update..."></textarea>
      </div>

      <div class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Tracking Number</label>
          <input type="text" class="form-control" formControlName="trackingNumber" 
                 placeholder="Enter tracking number">
        </div>
        <div class="col-md-6">
          <label class="form-label">Carrier</label>
          <input type="text" class="form-control" formControlName="carrier" 
                 placeholder="e.g., PostNord, DHL">
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-outline-secondary me-2" (click)="closeStatusModal()">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="statusForm.invalid">
          Update Status
        </button>
      </div>
    </form>
  </nb-card-body>
</nb-card>

<!-- Refund Modal -->
<nb-card *ngIf="showRefundModal" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5>Process Refund</h5>
    <button class="btn btn-ghost" (click)="closeRefundModal()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>
  <nb-card-body class="modal-body">
    <form [formGroup]="refundForm" (ngSubmit)="processRefund()">
      <div class="mb-3">
        <label class="form-label">Refund Type *</label>
        <select class="form-control" formControlName="refundType" required>
          <option value="partial">Partial Refund</option>
          <option value="full">Full Refund</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label">Refund Amount (DKK) *</label>
        <input type="number" class="form-control" formControlName="amount" 
               min="0.01" step="0.01" required>
        <small class="text-muted">Order total: {{ formatCurrency(selectedOrder?.finalAmount || 0) }}</small>
        <div *ngIf="refundForm.get('amount')?.invalid && refundForm.get('amount')?.touched" class="text-danger">
          Valid refund amount is required
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Reason *</label>
        <textarea class="form-control" formControlName="reason" rows="3" 
                  placeholder="Explain the reason for this refund..." required></textarea>
        <div *ngIf="refundForm.get('reason')?.invalid && refundForm.get('reason')?.touched" class="text-danger">
          Reason is required
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-outline-secondary me-2" (click)="closeRefundModal()">
          Cancel
        </button>
        <button type="submit" class="btn btn-danger" [disabled]="refundForm.invalid">
          Process Refund
        </button>
      </div>
    </form>
  </nb-card-body>
</nb-card>